import { useState, useEffect } from 'react'
import {
  COUNTRY_CUSTOMER_TESTIMONIALS,
  MIDDLE_EAST_COUNTRY,
  ELA_TESTIMONIALS,
  MATH_TESTIMONIALS,
  TEACHER_MQL_TESTIMONIALS,
} from '@lib/data/testimonials'
import { CustomerTestimonialType } from '@lib/types/testimonials'
import { TESTIMONIAL_ENUM } from '@lib/constants/testimonials'
import { staticFile } from '@common/utils'
import { FastlyImage } from '@components/common/image'
import useDetectCountry from '@hooks/useDetectCountry'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import MsgTrans from '@components/common/i18n/message-trans'
import { msgT } from '@lib/utils/i18n'
import { Trans } from '@lingui/macro'
import TrustBox from '@components/common/trust-box'
import { currentYear } from '@lib/utils/date.utils'

const IMAGE_PATH = '/images/landing-page/testimonials/'

type Props = {
  subject?: 'Coding' | 'Public Speaking & Communication' | 'Math'
  isTeacher?: boolean
}

export const TestimonialCard = ({
  subject = 'Coding',
  isTeacher,
}: Props): JSX.Element => {
  const [testimonial, setTestimonial] = useState<CustomerTestimonialType<
    LinguiJSMessageDescriptor
  > | null>(null)
  const { countryCode } = useDetectCountry()
  useEffect(() => {
    setTestimonial(() => {
      const subjectTestimonialsMap = {
        'Public Speaking & Communication': ELA_TESTIMONIALS,
        Math: MATH_TESTIMONIALS,
        Coding: COUNTRY_CUSTOMER_TESTIMONIALS,
      }
      const testimonialsData = isTeacher
        ? TEACHER_MQL_TESTIMONIALS
        : subjectTestimonialsMap[subject]

      if (Object.keys(testimonialsData).includes(countryCode)) {
        return testimonialsData[countryCode][
          Math.floor(Math.random() * testimonialsData[countryCode].length)
        ]
      } else if (
        MIDDLE_EAST_COUNTRY.includes(countryCode) &&
        Object.keys(testimonialsData).includes('MIDDLE_EAST')
      ) {
        return testimonialsData['MIDDLE_EAST'][
          Math.floor(Math.random() * testimonialsData['MIDDLE_EAST'].length)
        ]
      } else {
        return testimonialsData['DEFAULT'][
          Math.floor(Math.random() * testimonialsData['DEFAULT'].length)
        ]
      }
    })
  }, [countryCode, isTeacher, subject])

  return (
    <div className="md:pl-6">
      <div className="lg:max-w-sm rounded-md overflow-hidden bg-white border-2 border-grey-200 px-4 md:px-8">
        {testimonial && (
          <div className="flex flex-col py-4">
            <img
              src={staticFile('/images/landing-page/testimonials/quote.svg')}
              className="h-16 w-16"
              alt="Quote"
            />
            <p className="font-600 text-grey text-2xl pb-8">
              <MsgTrans msg={testimonial.testimonial} />
            </p>

            <div className="flex">
              <FastlyImage
                src={`${IMAGE_PATH}${testimonial.image}.png`}
                webpSrc={`${IMAGE_PATH}${testimonial.image}.webp`}
                alt={msgT(testimonial.name)}
                width={isTeacher ? 133 : 127}
                height={isTeacher ? 133 : 144}
              />

              <div className="flex flex-col justify-center px-4 text-grey-900 text-xl">
                <div className="font-600">
                  <MsgTrans msg={testimonial.name} />
                </div>
                {testimonial.type === TESTIMONIAL_ENUM.PARENT && (
                  <div className="font-400">
                    <Trans>Parent</Trans>
                  </div>
                )}
                {testimonial.type == TESTIMONIAL_ENUM.STUDENT && (
                  <div className="font-400">
                    <Trans>
                      Student <span className="text-grey-700">|</span> Grade{' '}
                      {testimonial.grade}
                    </Trans>
                  </div>
                )}
                {!testimonial?.type && testimonial?.designation && (
                  <div className="font-400">
                    <Trans>{testimonial.designation}</Trans>
                  </div>
                )}
                {testimonial.location && (
                  <div className="font-400">
                    <Trans>{testimonial.location}</Trans>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex mt-6">
        <TrustBox isDarkText />
      </div>
      <div className="flex mt-4">
        <p className="text-xs text-grey-800">
          <Trans>© {currentYear} Codingal Inc. (USA)</Trans>
        </p>
      </div>
    </div>
  )
}

export default TestimonialCard
